// Converte o SVG dinâmico do backend em PNG e dispara share/download.
// SVG endpoint: GET /api/og/professional/{id}.svg — 1080x1080 self-contained
// (foto embarcada em base64 no server) para evitar problemas de CORS/taint
// no canvas.
//
// Fluxo:
//   1. Fetch SVG como texto.
//   2. Cria Image do data-URL do SVG.
//   3. Desenha em <canvas> 1080x1080.
//   4. Exporta como PNG blob.
//   5. Se Web Share API suportar files (mobile) → navigator.share().
//      Senão → baixa PNG + copia link do perfil no clipboard.

const CANVAS_SIZE = 1080;

const svgToPngBlob = async (svgText) => {
  const svgBlob = new Blob([svgText], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);
  try {
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE);
    return await new Promise((res) => canvas.toBlob((b) => res(b), "image/png", 0.95));
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const shareProfessionalCard = async ({ apiBase, professionalId, displayName, profileUrl, whatsappUrl, variant = "raf" }) => {
  // variant="raf" → OG endpoint padrão RAF; variant="fp" → selo Filho Presente âmbar
  const endpoint = variant === "fp"
    ? `${apiBase}/api/og/fp-professional/${professionalId}.svg`
    : `${apiBase}/api/og/professional/${professionalId}.svg`;
  const resp = await fetch(endpoint);
  if (!resp.ok) throw new Error("Falha ao gerar imagem — perfil sem selo ativo?");
  const svgText = await resp.text();
  const pngBlob = await svgToPngBlob(svgText);
  if (!pngBlob) throw new Error("Falha ao renderizar PNG");

  const safeName = (displayName || "profissional").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  const brandPrefix = variant === "fp" ? "filho-presente" : "raf-direct";
  const fileName = `${brandPrefix}-${safeName}.png`;
  const file = new File([pngBlob], fileName, { type: "image/png" });

  // Texto para acompanhar o compartilhamento. Ordem: mensagem em cima,
  // nome+link embaixo. NÃO passa `url` no navigator.share pra não duplicar
  // o link (alguns apps mostram text + url separadamente).
  const shareText = variant === "fp"
    ? `Profissional Filho Presente — cuidado geriátrico verificado pela RAF Direct.\n\n${displayName}\n${profileUrl}`
    : `Consulta online com curadoria da RAF Direct — Perfil Verificado.\n\n${displayName}\n${profileUrl}`;

  // Tenta share nativo com arquivo (mobile — Instagram/WhatsApp/LinkedIn stories).
  // Não passamos `url` separado pra evitar duplicação (WhatsApp/iOS enviam
  // text + url como duas linhas repetidas).
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: displayName, text: shareText });
      return { mode: "shared" };
    } catch (e) {
      if (e?.name === "AbortError") return { mode: "cancelled" };
    }
  }

  // Fallback desktop: baixa PNG + copia texto completo (perfil + WhatsApp) no clipboard.
  const dl = document.createElement("a");
  dl.href = URL.createObjectURL(pngBlob);
  dl.download = fileName;
  document.body.appendChild(dl);
  dl.click();
  dl.remove();
  setTimeout(() => URL.revokeObjectURL(dl.href), 4000);

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareText);
      return { mode: "downloaded-and-copied" };
    }
  } catch { /* ignore */ }
  return { mode: "downloaded" };
};
